import React, { useEffect, useMemo, useState } from "react";
import { X, Send, FileText, Mail, AlertTriangle, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { sanitizeHtml } from "../lib/sanitize";

export default function BulkSendEmailDialog({ open, leadIds, sampleLead, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [gmailStatus, setGmailStatus] = useState({ connected: false });
  const [me, setMe] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [selectedAttIds, setSelectedAttIds] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [tpl, status, meRes] = await Promise.all([
          api.get("/email-templates"),
          api.get("/oauth/gmail/status").catch(() => ({ data: { connected: false } })),
          api.get("/auth/me").catch(() => ({ data: null })),
        ]);
        setTemplates(tpl.data || []);
        setGmailStatus(status.data || { connected: false });
        setMe(meRes.data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load templates");
      }
    })();
  }, [open]);

  // Reset form whenever opened
  useEffect(() => {
    if (open) {
      setTemplateId(""); setSubject(""); setBody("");
      setAttachments([]); setSelectedAttIds(new Set());
    }
  }, [open]);

  const onSelectTemplate = async (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      setSubject(t.subject);
      setBody(t.body);
      try {
        const { data } = await api.get(`/email-templates/${id}/attachments`);
        setAttachments(data || []);
        setSelectedAttIds(new Set((data || []).map((a) => a.id)));
      } catch {
        setAttachments([]);
        setSelectedAttIds(new Set());
      }
    } else {
      setAttachments([]);
      setSelectedAttIds(new Set());
    }
  };

  // Preview rendered against the FIRST selected lead so the user can see what they're sending.
  const previewCtx = useMemo(() => {
    if (!sampleLead) return {};
    return {
      company_name: sampleLead.company_name || "",
      client_poc_name: sampleLead.client_poc_name || "",
      client_poc_email: sampleLead.client_poc_email || "",
      lead_source: sampleLead.lead_source || "",
      lead_owner_name: sampleLead.lead_owner_name || "",
      rep_name: me?.name || "",
      rep_email: me?.email || "",
      rep_phone: me?.phone || "",
    };
  }, [sampleLead, me]);

  const render = (text) => {
    if (!text) return "";
    return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (m, key) => {
      const v = previewCtx[key];
      return v === undefined || v === null || v === "" ? m : String(v);
    });
  };

  const renderedSubject = render(subject);
  const renderedBody = render(body);

  const submit = async () => {
    if (!subject.trim()) return toast.error("Subject is required");
    if (!body.trim()) return toast.error("Body is required");
    if (!gmailStatus.connected) return toast.error("Connect Gmail first (Settings → Connect Gmail)");
    setSending(true);
    try {
      const payload = {
        lead_ids: leadIds,
        template_id: templateId || null,
        subject,
        body,
        attachment_ids: templateId ? Array.from(selectedAttIds) : null,
        skip_no_email: true,
      };
      const { data } = await api.post("/leads/bulk-send-email", payload);
      const skippedCount = data.skipped?.length || 0;
      toast.success(`Queued ${data.queued} email(s)${skippedCount ? ` · ${skippedCount} skipped` : ""}`);
      if (skippedCount) {
        const reasons = data.skipped.slice(0, 3).map((s) => `${s.company_name || "(unknown)"}: ${s.reason}`).join("; ");
        toast.message(`Skipped: ${reasons}${skippedCount > 3 ? "…" : ""}`);
      }
      onSent?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk send failed");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="bulk-send-email-dialog"
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !sending && onClose()}
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
              <Mail size={16} className="text-orange-600" /> Send bulk email
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">To <b>{leadIds.length}</b> selected lead(s) · variables render per recipient</p>
          </div>
          <button onClick={() => !sending && onClose()} className="p-1.5 rounded-md hover:bg-slate-100"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {!gmailStatus.connected && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700 inline-flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <div>Gmail is not connected. Go to <b>Settings → Connect Gmail</b> first.</div>
            </div>
          )}
          {gmailStatus.connected && (
            <div className="text-xs px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 inline-block">
              Sending from <b>{me?.email}</b>
            </div>
          )}

          {/* Template picker */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Template</label>
            <select
              data-testid="bulk-email-template-select"
              value={templateId}
              onChange={(e) => onSelectTemplate(e.target.value)}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md bg-white"
            >
              <option value="">— Start from scratch —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Subject</label>
            <input
              data-testid="bulk-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Hi {{client_poc_name}} — Quick intro"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Body (HTML)</label>
            <textarea
              data-testid="bulk-email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={9}
              placeholder="<p>Hi {{client_poc_name}},</p>"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md font-mono"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Vars: {"{{client_poc_name}}, {{company_name}}, {{rep_name}}, {{rep_phone}}, {{lead_source}}"}
            </p>
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 inline-flex items-center gap-1.5">
                <Paperclip size={12} /> Attachments ({selectedAttIds.size} of {attachments.length})
              </label>
              <div className="space-y-1.5 border border-slate-200 rounded-md p-2 max-h-32 overflow-y-auto bg-slate-50/40">
                {attachments.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedAttIds.has(a.id)}
                      onChange={() => {
                        const next = new Set(selectedAttIds);
                        if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
                        setSelectedAttIds(next);
                      }}
                      className="accent-orange-500"
                    />
                    <span className="text-slate-700 truncate">{a.filename}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Preview against first lead */}
          {sampleLead && (
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 inline-flex items-center gap-1.5">
                <FileText size={11} /> Live preview — first recipient: {sampleLead.company_name}
              </div>
              <div className="text-xs text-slate-800 font-medium mb-2">{renderedSubject || <span className="text-slate-400">(empty)</span>}</div>
              <div
                className="prose prose-sm max-w-none text-slate-700 text-xs"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderedBody) || "<span style='color:#94a3b8'>(empty)</span>" }}
              />
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="bulk-email-submit"
            onClick={submit}
            disabled={sending || !gmailStatus.connected}
            className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium inline-flex items-center gap-1.5"
          >
            {sending ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                  <path d="M12 2 a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                Queuing…
              </>
            ) : (
              <>
                <Send size={13} /> Send to {leadIds.length} lead(s)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
